/**
 * Edge Binding Utilities
 *
 * Provides binding operations for connectors/edges on connected diagrams.
 *
 * Two patterns:
 * - Entity-field: Relationship stored as field on entity record (e.g., employee.reportsTo).
 *   Drawing/deleting edges updates the entity's bound record field.
 * - Edge-table: Relationships stored as separate records in a different table.
 *   Edges get their own ShapeDataBinding in edge.attrs.dataBinding.
 */

import type * as Y from 'yjs';
import type {
  DomainDataProfile,
  EntityFieldRelationshipMapping,
  EdgeTableRelationshipMapping,
  RelationshipMapping,
} from '@/core/data-profiles/DomainDataProfile';
import type { ShapeDataBinding } from '../models/ShapeDataBinding';
import { createNewRecordBinding } from './createNewRecordBinding';
import { updateBoundRecordChanges } from '../stores/BoundRecordStore';
import { getDataBinding } from '../services/BoundShapeEditInterceptor';
import { readYjsValue } from '../../../lib/diagram/yjsCompat';

/**
 * Find the relationship mapping for a given edge type in a profile.
 */
export function resolveEdgeBinding(
  edgeType: string,
  profile: DomainDataProfile | undefined,
): RelationshipMapping | undefined {
  return profile?.relationshipMappings?.find(m => m.edgeType === edgeType);
}

/**
 * Entity-field pattern: update entity's bound record field when an edge is created/deleted.
 *
 * When an edge is created between two bound nodes, the "owner" node's bound record
 * gets a local change on the mapped field (e.g., reportsTo = manager's recordId).
 * When an edge is deleted, the field is cleared.
 *
 * Falls through silently if either node isn't bound.
 */
export function applyEntityFieldEdgeChange(
  doc: Y.Doc,
  mapping: EntityFieldRelationshipMapping,
  fromShapeId: string,
  toShapeId: string | null,
  sourceId: string,
  userId: string,
): void {
  const shapesMap = doc.getMap('shapes');

  const ownerShapeId = mapping.fieldOwner === 'from' ? fromShapeId : toShapeId;
  if (!ownerShapeId) return;

  const rawOwner = shapesMap.get(ownerShapeId);
  const ownerShape = rawOwner != null ? readYjsValue<Record<string, unknown>>(rawOwner) : undefined;
  if (!ownerShape) return;

  const ownerBinding = getDataBinding(ownerShape);
  if (!ownerBinding?.sourceId || !ownerBinding?.recordId) return;

  let fieldValue: string | null = null;
  if (toShapeId !== null) {
    const valueShapeId = mapping.fieldValue === 'from' ? fromShapeId : toShapeId;
    const rawValue = shapesMap.get(valueShapeId);
    const valueShape = rawValue != null ? readYjsValue<Record<string, unknown>>(rawValue) : undefined;
    if (valueShape) {
      const valueBinding = getDataBinding(valueShape);
      fieldValue = valueBinding?.recordId ?? null;
    }
  }

  updateBoundRecordChanges(
    doc,
    sourceId,
    ownerBinding.recordId,
    { [mapping.fieldName]: fieldValue ?? '' },
    ownerBinding.cachedRecord,
    userId,
  );
}

/**
 * Edge-table pattern: create a ShapeDataBinding for a connector.
 *
 * Pure function — returns binding data without writing to Yjs.
 * The caller is responsible for including the binding in edge.attrs.dataBinding
 * BEFORE calling MutationManager.addEdge().
 */
export function createEdgeBinding(
  edgeId: string,
  mapping: EdgeTableRelationshipMapping,
  fromRecordId: string,
  toRecordId: string,
  sourceId: string,
  userId: string,
): ShapeDataBinding {
  const initialData: Record<string, unknown> = {
    [mapping.sourceField]: fromRecordId,
    [mapping.targetField]: toRecordId,
  };
  if (mapping.typeField) {
    initialData[mapping.typeField] = mapping.edgeType;
  }

  return createNewRecordBinding({
    shapeId: edgeId,
    sourceId,
    userId,
    initialData,
  });
}

/**
 * Edge-table pattern: handle edge deletion by tracking the binding for commit.
 *
 * - If binding is for a new record (never committed), simply returns it (no delete needed).
 * - If binding is for an existing record, stores it in `deletedEdgeBindings` Yjs map
 *   so the commit flow can delete the record from the external source.
 *
 * Returns the removed binding, or null if the edge had no binding.
 */
export function removeBindingFromEdge(
  doc: Y.Doc,
  edgeId: string,
): ShapeDataBinding | null {
  const connectorsMap = doc.getMap('connectors');
  const edge = connectorsMap.get(edgeId) as Record<string, unknown> | undefined;
  if (!edge) return null;

  const attrs = edge.attrs as Record<string, unknown> | undefined;
  const binding = attrs?.dataBinding as ShapeDataBinding | undefined;
  if (!binding) return null;

  if (!binding.isNewRecord) {
    const deletedMap = doc.getMap('deletedEdgeBindings') as Y.Map<ShapeDataBinding>;
    doc.transact(() => {
      deletedMap.set(edgeId, binding);
    }, 'edge-binding-delete-track');
  }

  return binding;
}

/**
 * Get bound edges from the connectors map.
 */
export function getBoundEdges(
  doc: Y.Doc,
  sourceId: string,
): Array<{ edgeId: string; binding: ShapeDataBinding }> {
  const connectorsMap = doc.getMap('connectors');
  const result: Array<{ edgeId: string; binding: ShapeDataBinding }> = [];

  connectorsMap.forEach((edge, edgeId) => {
    const attrs = (edge as Record<string, unknown>).attrs as Record<string, unknown> | undefined;
    const binding = attrs?.dataBinding as ShapeDataBinding | undefined;
    if (binding && binding.sourceId === sourceId) {
      result.push({ edgeId, binding });
    }
  });

  return result;
}

/**
 * Get deleted edge bindings pending commit.
 */
export function getDeletedEdgeBindings(
  doc: Y.Doc,
  sourceId: string,
): Array<{ edgeId: string; binding: ShapeDataBinding }> {
  const deletedMap = doc.getMap('deletedEdgeBindings') as Y.Map<ShapeDataBinding>;
  const result: Array<{ edgeId: string; binding: ShapeDataBinding }> = [];

  deletedMap.forEach((binding, edgeId) => {
    if (binding.sourceId === sourceId) {
      result.push({ edgeId, binding });
    }
  });

  return result;
}

/**
 * Clear deleted edge bindings after successful commit.
 */
export function clearDeletedEdgeBindings(
  doc: Y.Doc,
  _sourceId: string,
  edgeIds: string[],
): void {
  const deletedMap = doc.getMap('deletedEdgeBindings') as Y.Map<ShapeDataBinding>;

  doc.transact(() => {
    for (const edgeId of edgeIds) {
      deletedMap.delete(edgeId);
    }
  }, 'edge-binding-delete-clear');
}
